/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo, useState } from 'react';
import { Popover } from 'antd';
import Button from 'client/component-library/buttons/ButtonAdmin/afsButton';
import { EntitySearch } from 'client/modules/cbi-entity/components/cbi-entity/EntitySearch';
import { useListFundingDraftQuery } from 'client/modules/cbi-entity/services/funding-service/hooks/UseListFundingDraftQuery';
import { DealCard as DealCardModel } from '../apis/GetDealQueue';
import {
  DEFAULT_MERGE_OPTIONS,
  UpdateMergeOptions,
  UpdateTarget,
  buildSuggestedTargets,
  fundingsToTargets,
  updateTargetKey,
} from '../utils/dealUpdate';

const OTHER = 'other';

interface DealUpdateMenuProps {
  card: DealCardModel;
  // The deal's own (extracted) entity -- the suggested targets belong to it. <= 0 when unresolved.
  idCbiEntity: number;
  onUpdate: (target: UpdateTarget, merge: UpdateMergeOptions) => void;
}

// How many distinct articles the cluster would add as news mentions.
const countArticles = (card: DealCardModel): number =>
  new Set(
    [card.anchor, ...card.memberDeals]
      .map((deal) => (deal.newsUrl || '').trim().toLowerCase())
      .filter(Boolean)
  ).size;

const MERGE_CHECKBOXES: Array<{
  key: keyof UpdateMergeOptions;
  label: (articles: number) => string;
}> = [
  {
    key: 'mentions',
    label: (articles) =>
      `News ${articles === 1 ? 'article' : 'articles'} (${articles})`,
  },
  { key: 'investors', label: () => 'Investors' },
  { key: 'amount', label: () => 'Amount' },
  { key: 'date', label: () => 'Date' },
  { key: 'round', label: () => 'Round' },
];

export const DealUpdatePanel = ({
  card,
  idCbiEntity,
  onUpdate,
}: DealUpdateMenuProps) => {
  const suggested = useMemo(
    () =>
      idCbiEntity > 0 ? buildSuggestedTargets(card.anchor, idCbiEntity) : [],
    [card.anchor, idCbiEntity]
  );

  // Selected radio: a suggested target's key, or OTHER. Defaults to the first suggestion.
  const [selected, setSelected] = useState<string>(
    suggested.length ? updateTargetKey(suggested[0]) : OTHER
  );
  const [merge, setMerge] = useState<UpdateMergeOptions>(DEFAULT_MERGE_OPTIONS);

  // "Other…": any entity, then one of its fundings.
  const [otherEntity, setOtherEntity] = useState<{
    idCbiEntity: number;
    name: string;
  } | null>(null);
  const [otherFundingKey, setOtherFundingKey] = useState('');
  const otherFundings = useListFundingDraftQuery(
    {
      idCbiEntity: otherEntity?.idCbiEntity ?? 0,
      idEntityDraft: '',
      includeInactive: true,
    },
    selected === OTHER && !!otherEntity
  );
  const otherTargets = useMemo(
    () =>
      otherEntity
        ? fundingsToTargets(otherFundings.data, otherEntity.idCbiEntity)
        : [],
    [otherFundings.data, otherEntity]
  );

  const chosen: UpdateTarget | undefined =
    selected === OTHER
      ? otherTargets.find((t) => updateTargetKey(t) === otherFundingKey)
      : suggested.find((t) => updateTargetKey(t) === selected);

  const articles = countArticles(card);

  let otherFundingsPlaceholder = 'Select a funding';
  if (otherFundings.isFetching) {
    otherFundingsPlaceholder = 'Loading fundings…';
  } else if (!otherTargets.length) {
    otherFundingsPlaceholder = 'No fundings found';
  }

  return (
    <div className="w-96 text-xs" data-test="deal-update-panel">
      <div className="font-semibold mb-1">Update an existing funding</div>

      <div className="flex flex-col gap-y-1">
        {suggested.map((target) => (
          <label
            key={updateTargetKey(target)}
            className="flex items-start gap-x-1"
          >
            <input
              type="radio"
              name="update-target"
              checked={selected === updateTargetKey(target)}
              onChange={() => setSelected(updateTargetKey(target))}
              data-test="update-target-option"
            />
            <span>{target.label}</span>
          </label>
        ))}
        <label className="flex items-start gap-x-1">
          <input
            type="radio"
            name="update-target"
            checked={selected === OTHER}
            onChange={() => setSelected(OTHER)}
            data-test="update-target-other"
          />
          <span>Other funding…</span>
        </label>
      </div>

      {selected === OTHER && (
        <div className="mt-2 flex flex-col gap-y-1 pl-4">
          {otherEntity ? (
            <div>
              <span className="font-semibold">{otherEntity.name}</span>{' '}
              <button
                type="button"
                className="text-blue-600 underline"
                onClick={() => {
                  setOtherEntity(null);
                  setOtherFundingKey('');
                }}
              >
                change
              </button>
            </div>
          ) : (
            <EntitySearch
              // Fundings belong to real entities only; hide draft-only entity results.
              searchDrafts={false}
              placeholder="Search for an entity..."
              onSelection={(_value, result) => {
                if (result?.idCbiEntity) {
                  setOtherEntity({
                    idCbiEntity: result.idCbiEntity,
                    name: result.name,
                  });
                  setOtherFundingKey('');
                }
              }}
            />
          )}
          {otherEntity && (
            <select
              className="border rounded p-1 w-full"
              value={otherFundingKey}
              onChange={(event) => setOtherFundingKey(event.target.value)}
              data-test="update-other-funding-select"
            >
              <option value="">{otherFundingsPlaceholder}</option>
              {otherTargets.map((target) => (
                <option
                  key={updateTargetKey(target)}
                  value={updateTargetKey(target)}
                >
                  {target.label}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <div className="mt-2 pt-2 border-t">
        <div className="text-gray-500 mb-1">Bring over from this deal</div>
        <div className="flex flex-row flex-wrap gap-x-3 gap-y-1">
          {MERGE_CHECKBOXES.map(({ key, label }) => (
            <label key={key} className="flex items-center gap-x-1">
              <input
                type="checkbox"
                checked={merge[key]}
                onChange={(event) =>
                  setMerge((current) => ({
                    ...current,
                    [key]: event.target.checked,
                  }))
                }
                data-test={`update-merge-${key}`}
              />
              {label(articles)}
            </label>
          ))}
        </div>
      </div>

      <div className="mt-2" style={{ width: 120 }}>
        <Button
          onClick={() => chosen && onUpdate(chosen, merge)}
          disabled={!chosen}
          data-test="update-deal-confirm"
        >
          Update
        </Button>
      </div>
    </div>
  );
};

export const DealUpdateMenu = (props: DealUpdateMenuProps) => {
  const [open, setOpen] = useState(false);
  const { onUpdate, ...rest } = props;

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="leftTop"
      destroyTooltipOnHide
      content={
        <DealUpdatePanel
          {...rest}
          onUpdate={(target, merge) => {
            setOpen(false);
            onUpdate(target, merge);
          }}
        />
      }
    >
      <div>
        <Button
          onClick={() => undefined}
          theme="success"
          data-test="update-deal-button"
        >
          Update ▾
        </Button>
      </div>
    </Popover>
  );
};
